'use strict';

/**
 * Boost / demote de visibilidad para proveedores scrapeados por ciudad/región.
 * Usado en backend (searchProperties) y frontend (useColivingSearchEngine).
 *
 * Orden final del bloque scraped:
 *   1. Afiliados boosted (Blueground + Outsite; Blueground en US/UK/ES Madrid+Barcelona)
 *   2. Integración booking directa (Homiii, si activa)
 *   3. Resto (interleaved round-robin o sort activo)
 *   4. Demoted al final (operadores que redirigen sin pasar por leads Coolliving)
 */

const BOOSTED_PROVIDER_RULES = {
  spacest: {
    regions: ['es'],
    cities: [], // todo ES — mismo alcance que Blueground; interleave 2:1 vs BG
  },
  outsite: {
    regions: ['es'],
    cities: ['barcelona'],
  },
  outsitenyc: {
    regions: ['us', 'uk'],
    cities: [],
  },
  blueground: {
    regions: ['us', 'uk', 'es'],
    cities: [],
  },
  /** NYC — mismo rol que Spacest en ES (partner prioritario regional) */
  aya: {
    regions: ['us'],
    cities: [],
  },
};

/**
 * Último bloque: operadores que envían tráfico fuera del funnel de leads.
 * Madrid: Kanso, Gaiastays | London: Coluxliving | NYC: top bypass US
 */
const DEMOTED_PROVIDER_RULES = {
  kanso: {
    regions: ['es'],
    cities: ['madrid'],
  },
  gaiastays: {
    regions: ['es'],
    cities: ['madrid'],
  },
  coluxliving: {
    regions: ['uk'],
    cities: [],
  },
  furnishedquarters: {
    regions: ['us'],
    cities: [],
  },
  junehomes: {
    regions: ['us'],
    cities: [],
  },
  squarelivinghub: {
    regions: ['us'],
    cities: [],
  },
};

/** Bonus en getMarketRelevanceScore (city*1000 + quality + bonus) */
const PROVIDER_BOOST_BONUS = 5000;
/**
 * Partner prioritario por región: Spacest (ES) y Hello AYA (US/NYC).
 * Mismo bonus en market_relevance e intercalado 2:1 vs Blueground.
 */
const REGION_PRIORITY_PARTNER_BOOST_BONUS = 8000;
/** @deprecated alias */
const SPACEST_BOOST_BONUS = REGION_PRIORITY_PARTNER_BOOST_BONUS;
/** @deprecated alias */
const AYA_BOOST_BONUS = REGION_PRIORITY_PARTNER_BOOST_BONUS;

/** Penalización si un demoted escapa al bucket regular (defensa en market_relevance) */
const PROVIDER_DEMOTE_PENALTY = 5000;

/**
 * Patrón de intercalado de proveedores boosted.
 * 2 Spacest por cada 1 Blueground; Outsite 1 slot cada 2 ciclos (prioridad baja).
 * Inicio siempre Spacest-heavy: S, S, BG, S, S, BG, outsite, …
 */
const BOOST_INTERLEAVE_ORDER = ['spacest', 'blueground', 'outsite', 'outsitenyc'];
const BOOST_INTERLEAVE_PATTERN = [
  'spacest', 'spacest', 'blueground',
  'spacest', 'spacest', 'blueground',
  'outsite',
];

/** US/NYC — espejo de BOOST_INTERLEAVE_PATTERN (AYA ≈ Spacest, outsitenyc ≈ outsite) */
const US_NYC_BOOST_INTERLEAVE_PATTERN = [
  'aya', 'aya', 'blueground',
  'aya', 'aya', 'blueground',
  'outsitenyc',
];

/**
 * Scrapeados ocultos del marketplace (Firestore intacto; isActive puede seguir true).
 * Filtrado en searchProperties + scrapers con enabled: false en registry.
 */
const MARKETPLACE_HIDDEN_PROVIDERS = [
  'sharedeasy',
  'hive',
  'gaiastays',
  'trc',
  'thr',
];

function normalizeProvider(source) {
  return (source || '').toLowerCase().trim();
}

function normalizeCityKey(location) {
  if (!location || typeof location !== 'string') return '';
  return location
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function matchesBoostCity(listing, cities) {
  if (!cities.length) return true;
  const city = normalizeCityKey(listing.propertyLocation);
  const neighborhood = normalizeCityKey(listing.propertyNeighborhood || '');
  const title = normalizeCityKey(listing.title || '');
  const haystack = `${city} ${neighborhood} ${title}`;

  return cities.some((c) => {
    const key = normalizeCityKey(c);
    return city === key || haystack.includes(key);
  });
}

function matchesProviderRule(listing, rules) {
  if (!listing || !rules) return false;

  if (rules.regions?.length && listing.region && !rules.regions.includes(listing.region)) {
    return false;
  }

  return matchesBoostCity(listing, rules.cities || []);
}

function isBoostedProvider(listing) {
  const source = normalizeProvider(listing.externalSource || listing.source);
  if (!source) return false;
  const rules = BOOSTED_PROVIDER_RULES[source];
  if (!rules) return false;
  return matchesProviderRule(listing, rules);
}

function isDemotedProvider(listing) {
  const source = normalizeProvider(listing.externalSource || listing.source);
  if (!source) return false;
  const rules = DEMOTED_PROVIDER_RULES[source];
  if (!rules) return false;
  return matchesProviderRule(listing, rules);
}

function isMarketplaceHiddenProvider(listing) {
  const source = normalizeProvider(listing.externalSource || listing.source);
  if (!source) return false;
  return MARKETPLACE_HIDDEN_PROVIDERS.includes(source);
}

function getProviderBoostBonus(listing) {
  if (!isBoostedProvider(listing)) return 0;
  const source = normalizeProvider(listing.externalSource || listing.source);
  if (source === 'aya' || source === 'spacest') return REGION_PRIORITY_PARTNER_BOOST_BONUS;
  return PROVIDER_BOOST_BONUS;
}

function resolveBoostInterleavePattern(byProvider) {
  if (byProvider.aya?.length && !byProvider.spacest?.length) {
    return US_NYC_BOOST_INTERLEAVE_PATTERN;
  }
  return BOOST_INTERLEAVE_PATTERN;
}

function getProviderDemotePenalty(listing) {
  return isDemotedProvider(listing) ? PROVIDER_DEMOTE_PENALTY : 0;
}

/** @deprecated Use partitionScrapedBlock */
function partitionBoostedScraped(items) {
  const { boosted, regular, demoted } = partitionScrapedBlock(items);
  return { boosted, regular: [...regular, ...demoted] };
}

function partitionScrapedBlock(items) {
  const boosted = [];
  const regular = [];
  const demoted = [];

  for (const item of items) {
    if (isBoostedProvider(item)) {
      boosted.push(item);
    } else if (isDemotedProvider(item)) {
      demoted.push(item);
    } else {
      regular.push(item);
    }
  }

  return { boosted, regular, demoted };
}

function getListingProviderKey(listing) {
  return normalizeProvider(listing.externalSource || listing.source);
}

/**
 * Intercala listings boosted siguiendo BOOST_INTERLEAVE_PATTERN (2 Spacest : 1 BG).
 * El patrón se repite hasta agotar buckets del patrón; slots vacíos se saltan sin cortar el ciclo.
 * Proveedores fuera del patrón (p. ej. outsitenyc) al final.
 */
function interleaveBoostedProviders(items) {
  if (!items?.length) return [];
  if (items.length === 1) return items;

  const byProvider = {};
  for (const item of items) {
    const key = getListingProviderKey(item);
    if (!byProvider[key]) byProvider[key] = [];
    byProvider[key].push(item);
  }

  const providerKeys = Object.keys(byProvider);
  if (providerKeys.length <= 1) return items;

  const cursors = {};
  for (const key of providerKeys) cursors[key] = 0;

  const activePattern = resolveBoostInterleavePattern(byProvider);
  const patternProviders = new Set(activePattern);
  const interleaved = [];
  let patternIdx = 0;
  const maxSteps = items.length * activePattern.length + 32;

  function hasRemainingInPatternProviders() {
    for (const p of patternProviders) {
      const bucket = byProvider[p];
      if (bucket && cursors[p] < bucket.length) return true;
    }
    return false;
  }

  for (let step = 0; step < maxSteps && hasRemainingInPatternProviders(); step++) {
    const slot = activePattern[patternIdx % activePattern.length];
    patternIdx++;
    const bucket = byProvider[slot];
    if (!bucket || cursors[slot] >= bucket.length) continue;
    interleaved.push(bucket[cursors[slot]]);
    cursors[slot]++;
  }

  for (const key of providerKeys) {
    const bucket = byProvider[key];
    while (cursors[key] < bucket.length) {
      interleaved.push(bucket[cursors[key]]);
      cursors[key]++;
    }
  }

  return interleaved;
}

function finalizeScrapedBlock(boosted, regularOrdered, demotedOrdered, bookingEnabled) {
  const boostedOrdered = interleaveBoostedProviders(boosted);
  const demoted = demotedOrdered || [];

  if (!bookingEnabled) {
    return [...boostedOrdered, ...regularOrdered, ...demoted];
  }

  const withBooking = regularOrdered.filter((c) => c.bookingIntegration);
  const regular = regularOrdered.filter((c) => !c.bookingIntegration);
  return [...boostedOrdered, ...withBooking, ...regular, ...demoted];
}

module.exports = {
  BOOSTED_PROVIDER_RULES,
  DEMOTED_PROVIDER_RULES,
  MARKETPLACE_HIDDEN_PROVIDERS,
  PROVIDER_BOOST_BONUS,
  REGION_PRIORITY_PARTNER_BOOST_BONUS,
  SPACEST_BOOST_BONUS,
  AYA_BOOST_BONUS,
  PROVIDER_DEMOTE_PENALTY,
  BOOST_INTERLEAVE_ORDER,
  BOOST_INTERLEAVE_PATTERN,
  US_NYC_BOOST_INTERLEAVE_PATTERN,
  isBoostedProvider,
  isDemotedProvider,
  isMarketplaceHiddenProvider,
  getProviderBoostBonus,
  getProviderDemotePenalty,
  partitionBoostedScraped,
  partitionScrapedBlock,
  interleaveBoostedProviders,
  finalizeScrapedBlock,
};
